import { CommonModule } from '@angular/common';
import { Component, OnInit, HostListener, Output, EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { PreVendaService } from '../../../services/pre-venda.service';
import { PreVenda } from '../../../models/pre-venda.model';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-import-pre-venda-pdv',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './import-pre-venda-pdv.component.html'
})
export class ImportPreVendaPdvComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();
  @Output() imported = new EventEmitter<PreVenda>();

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  filterTerm = '';
  preVendas: PreVenda[] = [];
  selectedPreVenda: PreVenda | null = null;
  selectedItemIndex = -1;
  loading = false;

  constructor(
    private preVendaService: PreVendaService,
    private messageService: MessageService
  ) {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((term: string) => this.loadPreVendas(term));
  }

  ngOnInit(): void {
    this.loadPreVendas(this.filterTerm);
  }

  private getStartOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private getEndOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  loadPreVendas(search: string): void {
    this.loading = true;
    const today = new Date();
    const initialDate = new Date(today);
    initialDate.setDate(initialDate.getDate() - 90);
    const finalDate = this.getEndOfDay(today);
    this.preVendaService.getAll(search, this.getStartOfDay(initialDate), finalDate, false, 1).subscribe({
      next: (response) => {
        this.preVendas = response.data ?? [];
        this.selectedItemIndex = this.preVendas.length > 0 ? 0 : -1;
        this.selectedPreVenda = this.preVendas.length > 0 ? this.preVendas[0] : null;
        this.loading = false;
      },
      error: () => {
        this.preVendas = [];
        this.selectedItemIndex = -1;
        this.selectedPreVenda = null;
        this.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.searchSubject.next(this.filterTerm);
  }

  voltar(): void {
    this.closed.emit();
  }

  importar(): void {
    if (!this.selectedPreVenda?.id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: 'Selecione uma pré-venda para importar.',
        life: 4000
      });
      return;
    }
    this.loading = true;
    this.preVendaService.getById(this.selectedPreVenda.id).subscribe({
      next: (response) => {
        const full = response.data;
        if (full?.id != null) {
          this.imported.emit(full);
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso!',
            detail: 'Pré-venda importada no PDV.',
            life: 3000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro!',
            detail: 'Não foi possível carregar a pré-venda.',
            life: 5000
          });
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: 'Não foi possível carregar a pré-venda. Tente novamente.',
          life: 5000
        });
      }
    });
  }

  onRowSelect(preVenda: PreVenda, index: number): void {
    this.selectedPreVenda = preVenda;
    this.selectedItemIndex = index;
  }

  get totalLista(): number {
    return this.preVendas.reduce((sum, p) => sum + (p.netTotal ?? 0), 0);
  }

  private navigateDown(): void {
    if (this.preVendas.length > 0) {
      this.selectedItemIndex = Math.min(this.selectedItemIndex + 1, this.preVendas.length - 1);
      this.selectedPreVenda = this.preVendas[this.selectedItemIndex];
      this.scrollToSelected();
    }
  }

  private navigateUp(): void {
    if (this.preVendas.length > 0) {
      this.selectedItemIndex = Math.max(this.selectedItemIndex - 1, -1);
      this.selectedPreVenda = this.selectedItemIndex >= 0 ? this.preVendas[this.selectedItemIndex] : null;
      this.scrollToSelected();
    }
  }

  private scrollToSelected(): void {
    if (this.selectedItemIndex >= 0 && this.selectedItemIndex < this.preVendas.length) {
      setTimeout(() => {
        const element = document.getElementById(`pre-venda-item-${this.selectedItemIndex}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 0);
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.voltar();
      return;
    }

    if (this.preVendas.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      if (event.key === 'ArrowDown') {
        this.navigateDown();
      } else {
        this.navigateUp();
      }
      return;
    }

    if (event.key === 'Enter' && this.preVendas.length > 0 && this.selectedItemIndex >= 0) {
      event.preventDefault();
      event.stopPropagation();
      this.importar();
    }
  }
}
